import React, { useCallback, useMemo } from 'react'
import { Button } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import { useAppSelector } from '../../../app/hooks'
import sensors from '../../../utils/sensors'
import { usePayment } from './hooks'

interface QuickPickerItem {
  text: string
  clickItem: string
  startTime: () => Dayjs
  endTime: () => Dayjs
  width?: string
  months?: number
}

const defaultQuickPickerItems: QuickPickerItem[] = [
  {
    text: '今天',
    clickItem: 'today',
    startTime: () => dayjs().startOf('day'),
    endTime: () => dayjs().endOf('day'),
    width: 'auto'
  },
  {
    text: '昨天',
    clickItem: 'yesterday',
    startTime: () => dayjs().add(-1, 'day').startOf('day'),
    endTime: () => dayjs().add(-1, 'day').endOf('day'),
    width: 'auto'
  },
  {
    text: '本周',
    clickItem: 'thisWeek',
    startTime: () => dayjs().startOf('weeks'),
    endTime: () => dayjs().endOf('days'),
    width: 'auto'
  },
  {
    text: '上周',
    clickItem: 'lastWeek',
    startTime: () => dayjs().add(-1, 'weeks').startOf('weeks'),
    endTime: () => dayjs().add(-1, 'weeks').endOf('weeks'),
    width: 'auto'
  },
  {
    text: '本月',
    clickItem: 'thisMonth',
    startTime: () => dayjs().startOf('month'),
    endTime: () => dayjs().endOf('days'),
    width: 'auto'
  },
  {
    text: '上月',
    clickItem: 'lastMonth',
    startTime: () => dayjs().add(-1, 'month').startOf('month'),
    endTime: () => dayjs().add(-1, 'month').endOf('month'),
    width: 'auto'
  },
  {
    text: '今年',
    clickItem: 'thisYear',
    startTime: () => dayjs().startOf('year'),
    endTime: () => dayjs().endOf('days'),
    width: 'auto',
    months: 12
  },
  {
    text: '去年',
    clickItem: 'lastYear',
    startTime: () => dayjs().add(-1, 'year').startOf('year'),
    endTime: () => dayjs().add(-1, 'year').endOf('year'),
    width: 'auto',
    months: 12
  },
  {
    text: '过去7天',
    clickItem: 'past7days',
    startTime: () => dayjs().add(-7, 'days').startOf('days'),
    endTime: () => dayjs().add(-1, 'days').endOf('days'),
    width: 'auto'
  },
  {
    text: '过去30天',
    clickItem: 'past30days',
    startTime: () => dayjs().add(-30, 'days').startOf('days'),
    endTime: () => dayjs().add(-1, 'days').endOf('days'),
    width: 'auto'
  },
  {
    text: '过去2个月',
    clickItem: 'past2months',
    startTime: () => dayjs().add(-2, 'months').startOf('days'),
    endTime: () => dayjs().add(-1, 'days').endOf('days'),
    width: 'auto',
    months: 2
  },
  {
    text: '过去3个月',
    clickItem: 'past3months',
    startTime: () => dayjs().add(-3, 'months').startOf('days'),
    endTime: () => dayjs().add(-1, 'days').endOf('days'),
    width: 'auto',
    months: 3
  },
  {
    text: '过去6个月',
    clickItem: 'pastHalfYear',
    startTime: () => dayjs().subtract(6, 'months').startOf('day'),
    endTime: () => dayjs().endOf('day'),
    width: 'auto',
    months: 6
  },
  {
    text: '过去一年',
    clickItem: 'past1year',
    startTime: () => dayjs().add(-1, 'years').startOf('days'),
    endTime: () => dayjs().add(-1, 'days').endOf('days'),
    width: 'auto',
    months: 12
  },
  {
    text: '所有时间',
    clickItem: 'allPhoto',
    startTime: () => dayjs('1970-01-01'),
    endTime: () => dayjs().endOf('days'),
    width: 'auto',
    months: 12 * 50
  }
]

interface QuickPickerButtonProps {
  text: string
  clickItem: string
  onClick: () => void
  selected: boolean
  dataStatisticsType?: string
}

const QuickPickerButton: React.FC<QuickPickerButtonProps> = ({
  text,
  clickItem,
  onClick,
  selected,
  dataStatisticsType
}) => {
  const isVip = useAppSelector((state) => state.group.isVip)

  return (
    <Button
      type="primary"
      className={selected ? 'quick-picker-btn-selected' : ''}
      onClick={() => {
        sensors.track('web_workgroup_filter_time_page_click', {
          clickItem,
          pagePath: dataStatisticsType
            ? dataStatisticsType
            : window.location.pathname,
          isVip
        })
        onClick()
      }}
    >
      {text}
    </Button>
  )
}

interface QuickPickerProps {
  currentDates: [Dayjs, Dayjs]
  maxMonths?: number
  onChange: (startDate: Dayjs, endDate: Dayjs) => void
  dataStatisticsType?: string
  isAllPhoto?: boolean
  disabledDate?: (date: Dayjs) => boolean
  quickPickerItems?: QuickPickerItem[]
  singleDayOnly?: boolean
}

const QuickPicker: React.FC<QuickPickerProps> = (props) => {
  const {
    currentDates,
    maxMonths,
    onChange,
    dataStatisticsType,
    isAllPhoto,
    disabledDate,
    singleDayOnly
  } = props
  const daysLimit = parseInt(
    useAppSelector(
      (state) => state.main.config?.normal_group_data_limit_days
    ) as string
  )
  const isVip = useAppSelector((state) => state.group.isVip)
  const onPay = usePayment()

  const quickPickerItems = useMemo(() => {
    const base = props.quickPickerItems || defaultQuickPickerItems
    if (singleDayOnly) {
      return base.filter((item) =>
        ['today', 'yesterday'].includes(item.clickItem)
      )
    }
    return base
  }, [props.quickPickerItems, singleDayOnly])

  // 检查快速选择项是否应该被禁用（基于disabledDate函数）
  const isQuickPickerItemDisabled = useCallback(
    (item: QuickPickerItem) => {
      // 如果没有设置disabledDate，则不禁用
      if (!disabledDate) {
        return false
      }

      // 检查快速选择项的开始时间是否被禁用
      const startTime = item.startTime()
      return disabledDate(startTime)
    },
    [disabledDate]
  )

  const onClick = useCallback(
    (startTime: Dayjs, endTime: Dayjs) => {
      if (!isVip && !isAllPhoto && dayjs().diff(startTime, 'day') > daysLimit) {
        onPay()
        return
      } else {
        onChange(startTime, endTime)
      }
    },
    [isVip, isAllPhoto, onPay, onChange, daysLimit]
  )

  return (
    <div className="quick-picker-wrapper">
      <h4>快速选择</h4>
      <div className="buttons">
        {quickPickerItems
          .filter((item) => {
            //考勤统计 和 照片台账表 最多允许1个月(31天)
            if (maxMonths === undefined) {
              return true
            }
            return item.months === undefined || item.months <= maxMonths
          })
          // 过滤掉开始日期被禁用的快速选择项
          .filter((item) => !isQuickPickerItemDisabled(item))
          .map((item, index) => (
            <QuickPickerButton
              key={index}
              text={item.text}
              clickItem={item.clickItem}
              selected={
                currentDates[0].isSame(item.startTime(), 'second') &&
                currentDates[1].isSame(item.endTime(), 'second')
              }
              onClick={() => onClick(item.startTime(), item.endTime())}
              dataStatisticsType={dataStatisticsType}
            />
          ))}
      </div>
    </div>
  )
}

export default QuickPicker
